import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useConfirm } from "../ui-components/ConfirmDialog";
import { Responsive, WidthProvider } from "react-grid-layout";
import { shallow } from "zustand/shallow";
import clsx from "clsx";
import useTabStore from "../stores/useTabStore";
import { useHasInternalNewsquawkUserRole } from "../hooks/useHasInternalNewsquawkUserRole";
import { useHasBetaWidgetsRole } from "../hooks/useHasBetaWidgetsRole";
import { WidgetContainer } from "./WidgetContainer";
import {
  extractBaseId,
  getWidgetById,
  isBetaPreviewWidget,
  isInternalOnlyWidget,
} from "../utils/widgets";
import { componentMap } from "../utils/componentMap";
import PropTypes from "prop-types";
import { destroyPersistentState } from "../hooks/useStateWithOptionalPersistence";
import { MobileWidgetPagination } from "../ui-components/MobileWidgetPagination";

const ResponsiveGridLayout = WidthProvider(Responsive);
const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const cols = { lg: 16, md: 10, sm: 6, xs: 2, xxs: 1 };

// Dynamic row height configuration
const HEADER_HEIGHT = 40; // Header.jsx: h-10 = 2.5rem = 40px
const TAB_BAR_HEIGHT = 28; // TabSystem.jsx actual height
const MARGIN = 3;
const TARGET_ROWS = 10; // Default layouts use h=10 for full-height widgets

/**
 * Calculate row height dynamically based on viewport
 * Ensures layouts fit within viewport on short screens
 */
const calculateRowHeight = () => {
  const availableHeight = window.innerHeight - HEADER_HEIGHT - TAB_BAR_HEIGHT;
  const calculatedHeight = Math.floor(
    (availableHeight - TARGET_ROWS * MARGIN) / TARGET_ROWS
  );
  // Clamp between 60-100px (min 60px ensures widget headers always fit)
  return Math.max(60, Math.min(calculatedHeight, 100));
};

const TabContent = ({ tab }) => {
  const confirm = useConfirm();
  const internalUser = useHasInternalNewsquawkUserRole();
  const betaUser = useHasBetaWidgetsRole();

  // Beta widgets are stripped from the layout entirely (not rendered as a
  // locked/upgrade placeholder) — same treatment as internal-only widgets, so a
  // shared or imported layout can't advertise them to users without access.
  const isWidgetHidden = useCallback(
    (widgetId) =>
      (isInternalOnlyWidget(widgetId) && !internalUser) ||
      (isBetaPreviewWidget(widgetId) && !internalUser && !betaUser),
    [internalUser, betaUser]
  );

  const { updateLayout, removeWidget } = useTabStore(
    (state) => ({
      updateLayout: state.updateLayout,
      removeWidget: state.removeWidget,
    }),
    shallow
  );

  const [gridWidth, setGridWidth] = useState(window.innerWidth);
  const [rowHeight, setRowHeight] = useState(calculateRowHeight);
  const [activeWidgetIndex, setActiveWidgetIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const gridRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (gridRef.current) {
        setGridWidth(gridRef.current.offsetWidth);
      }
      setRowHeight(calculateRowHeight());
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Track active widget via intersection observer (mobile only)
  useEffect(() => {
    if (!isMobile || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const index = parseInt(entry.target.dataset.widgetIndex || "0");
            setActiveWidgetIndex(index);
          }
        });
      },
      {
        root: containerRef.current,
        threshold: 0.5,
      }
    );

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const widgets = containerRef.current?.querySelectorAll(".react-grid-item");
      widgets?.forEach((el, i) => {
        el.dataset.widgetIndex = i.toString();
        observer.observe(el);
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [isMobile, tab.enabledWidgets]);

  // Scroll to widget function (horizontal)
  const scrollToWidget = useCallback((index) => {
    const widgets = containerRef.current?.querySelectorAll(".react-grid-item");
    widgets?.[index]?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  }, []);

  const onLayoutChange = (currentLayout, allLayouts) => {
    updateLayout(tab.id, allLayouts);
  };

  // Count visible widgets for pagination
  const visibleWidgetCount = tab.enabledWidgets?.filter((widgetCompositeId) => {
    const widgetId = extractBaseId("" + widgetCompositeId);
    if (isWidgetHidden(widgetId)) return false;
    return getWidgetById(widgetId) !== undefined;
  }).length || 0;

  return (
    <div
      ref={(el) => {
        gridRef.current = el;
        containerRef.current = el;
      }}
      className={clsx(
        "flex-none z-0",
        isMobile ? "mobile-carousel-container" : "overflow-auto"
      )}
    >
      {/* Pagination dots - mobile only */}
      {isMobile && (
        <MobileWidgetPagination
          total={visibleWidgetCount}
          current={activeWidgetIndex}
          onDotClick={scrollToWidget}
        />
      )}

      <div>
        <ResponsiveGridLayout
          className="layout min-h-screen"
          layouts={tab.layout}
          breakpoints={breakpoints}
          cols={cols}
          rowHeight={rowHeight}
          margin={[3, 3]}
          width={gridWidth}
          onLayoutChange={onLayoutChange}
          draggableCancel=".no-drag"
        >
          {tab.enabledWidgets?.map((widgetCompositeId) => {
            const widgetId = extractBaseId("" + widgetCompositeId);

            if (isWidgetHidden(widgetId)) {
              return null;
            }

            const widget = getWidgetById(widgetId);
            if (!widget) {
              return null;
            }

            const WidgetComponent = componentMap[widget.component];
            const SearchComponent = WidgetComponent.SearchComponent || null;

            return (
              <div key={widgetCompositeId}>
                <WidgetContainer
                  widget={widget}
                  widgetCompositeId={widgetCompositeId}
                  onClose={async () => {
                    const ok = await confirm({ title: "Remove Widget", message: "This widget will be removed from your tab. You can add it back from Manage Widgets.", confirmLabel: "Remove", variant: "warning" });
                    if (!ok) return;
                    removeWidget(tab.id, widgetCompositeId);
                    destroyPersistentState(widgetCompositeId);
                    destroyPersistentState(`tvAdvancedChart_lastSymbol_${widgetCompositeId}`);
                    destroyPersistentState(`broker:symbol:${widgetCompositeId}`);
                    destroyPersistentState(`broker:auto-switch:${widgetCompositeId}`);
                    destroyPersistentState(`economic-calendar.selectedCountries:${widgetCompositeId}`);
                    destroyPersistentState(`economic-calendar-v2.selectedCountries:${widgetCompositeId}`);
                  }}
                  searchComponent={SearchComponent}
                >
                  {(options) => (
                    <Suspense
                      fallback={
                        <div className="flex items-center justify-center h-full py-4">
                          <div className="flex items-center gap-2 text-gray-400 text-xs">
                            <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Loading {widget.name}...
                          </div>
                        </div>
                      }
                    >
                      <WidgetComponent
                        headerOptions={options}
                        widgetCompositeId={widgetCompositeId}
                      />
                    </Suspense>
                  )}
                </WidgetContainer>
              </div>
            );
          })}
        </ResponsiveGridLayout>
      </div>
    </div>
  );
};

TabContent.propTypes = {
  tab: PropTypes.shape({
    id: PropTypes.string.isRequired,
    layout: PropTypes.object.isRequired,
    enabledWidgets: PropTypes.array,
  }).isRequired,
};

export default TabContent;
